import type {
  SpreadsheetColumn,
  SpreadsheetColumnValueType,
  SpreadsheetSelectOption,
} from './types.ts';

const MAX_SUGGEST = 50;
const MAX_LINK_HREF = 2048;

export function columnValueType(
  column: SpreadsheetColumn | undefined,
): SpreadsheetColumnValueType {
  return column?.valueType ?? 'text';
}

export function isLinkColumn(column: SpreadsheetColumn | undefined): boolean {
  return columnValueType(column) === 'link';
}

/**
 * URLs a link-column button may open. Same rules as partner chrome actions:
 * `http:`, `https:`, `mailto:`, or a same-origin path starting with `/`.
 */
export function isSafeLinkHref(href: string): boolean {
  const t = href.trim();
  if (!t || t.length > MAX_LINK_HREF) return false;
  if (/[\u0000-\u001f\u007f]/.test(t)) return false;
  if (t.startsWith('/')) {
    return !t.startsWith('//') && !t.includes('\\');
  }
  try {
    const u = new URL(t);
    return u.protocol === 'https:' || u.protocol === 'http:' || u.protocol === 'mailto:';
  } catch {
    return false;
  }
}

export function isSelectColumn(column: SpreadsheetColumn | undefined): boolean {
  return (
    columnValueType(column) === 'select' &&
    (column?.selectOptions?.length ?? 0) > 0
  );
}

export function parseCommittedCellValue(
  column: SpreadsheetColumn | undefined,
  raw: string,
): { ok: true; value: string | number } | { ok: false } {
  const vt = columnValueType(column);
  if (vt === 'text') {
    return { ok: true, value: raw };
  }
  if (vt === 'number') {
    const t = raw.trim();
    if (t === '') return { ok: true, value: '' };
    const n = Number(t);
    if (!Number.isFinite(n)) return { ok: false };
    return { ok: true, value: n };
  }
  if (vt === 'link') {
    const t = raw.trim();
    if (t === '') return { ok: true, value: '' };
    if (!isSafeLinkHref(t)) return { ok: false };
    return { ok: true, value: t };
  }
  const options = column?.selectOptions ?? [];
  const t = raw.trim();
  if (t === '') {
    if (column?.allowEmpty !== false) return { ok: true, value: '' };
    const first = options[0];
    if (first) return { ok: true, value: first.value };
    return { ok: false };
  }
  const match = options.find((o) => o.value === t);
  if (match) return { ok: true, value: match.value };
  const tLower = t.toLowerCase();
  const byDisplay = options.find((o) => (o.label ?? o.value).toLowerCase() === tLower);
  if (byDisplay) return { ok: true, value: byDisplay.value };
  return { ok: false };
}

export function resolveSelectLabel(
  column: SpreadsheetColumn | undefined,
  storedValue: string,
): string {
  if (columnValueType(column) !== 'select') return storedValue;
  const t = storedValue.trim();
  const opt = column?.selectOptions?.find((o) => o.value === t);
  return opt?.label ?? opt?.value ?? storedValue;
}

/**
 * Plain text used for Find-in-sheet: matches what users see (select labels via `resolveSelectLabel`;
 * numbers and text as trimmed string form).
 */
export function cellPlainTextForSearch(
  column: SpreadsheetColumn | undefined,
  raw: string | number | undefined,
): string {
  if (raw === undefined) return '';
  const vt = columnValueType(column);
  if (vt === 'number') {
    if (typeof raw === 'number') return Number.isFinite(raw) ? String(raw) : '';
    return raw.trim();
  }
  const asString = typeof raw === 'number' ? String(raw) : raw;
  if (vt === 'select') {
    const t = asString.trim();
    if (!t) return '';
    return resolveSelectLabel(column, t).trim();
  }
  return asString.trim();
}

/** Prefix matches first, then substring; case-insensitive on value and label. */
export function filterSelectOptions(
  column: SpreadsheetColumn | undefined,
  query: string,
): SpreadsheetSelectOption[] {
  if (!isSelectColumn(column)) return [];
  const options = column!.selectOptions!;
  const q = query.trim().toLowerCase();
  if (!q) return [...options].slice(0, MAX_SUGGEST);
  const prefix: SpreadsheetSelectOption[] = [];
  const substr: SpreadsheetSelectOption[] = [];
  for (const o of options) {
    const v = o.value.toLowerCase();
    const l = (o.label ?? o.value).toLowerCase();
    if (v.startsWith(q) || l.startsWith(q)) prefix.push(o);
    else if (v.includes(q) || l.includes(q)) substr.push(o);
  }
  return [...prefix, ...substr].slice(0, MAX_SUGGEST);
}
